import { setCurrencyCountry, setTenantCurrency } from '../utils/currency';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { setTenantTimezone } from '../utils/tenantTime';
import { applyTenantAmbiance } from '../utils/ambiance';
import { getApiUrl as getApiUrlFromUtils } from '../utils/api';

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  // Return default values if not in provider (for admin routes)
  if (!context) {
    return {
      tenant: null,
      tenantSlug: null,
      isAdminMode: true,
      loading: false,
      error: null
    };
  }
  return context;
};

// Default branding for fallback
const DEFAULT_BRANDING = {
  name: '360 Resto',
  primary_color: 'var(--brand)',
  secondary_color: '#F5F5F5',
  accent_color: '#333333',
  logo_type: 'default'
};

export const TenantProvider = ({ children }) => {
  const { tenantSlug } = useParams();
  const location = useLocation();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if we're in admin mode
  const isAdminMode = location.pathname.startsWith('/admin');

  useEffect(() => {
    const loadTenant = async () => {
      // Skip tenant loading for admin routes
      if (isAdminMode || !tenantSlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const API = getApiUrlFromUtils();
        const response = await axios.get(`${API}/tenants/${tenantSlug}`, {withCredentials:true});
        const tenantData = response.data;
        window.__TENANT_SLUG__ = tenantData?.slug;
        setTenant(tenantData);
        setTenantTimezone(tenantData?.timezone);
        window.__tenantName = tenantData?.name;
        try { localStorage.setItem(`chargement42:${tenantData?.slug}`, JSON.stringify({ nom: tenantData?.name, logo: tenantData?.branding?.logo_url || tenantData?.logo_url || null })); } catch { /* stockage indisponible */ }  // LOT 42 · KI10
        applyTenantAmbiance(tenantData);
        
        // Apply tenant branding to CSS variables
        const branding = tenantData.branding || tenantData;
        if (branding.primary_color) {
          document.documentElement.style.setProperty('--tenant-primary', branding.primary_color);
          const hex = branding.primary_color.replace('#', '');
          if (/^[0-9a-f]{6}$/i.test(hex)) {
            const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
            const mix = (c, t, k) => Math.round(c + (t - c) * k);
            const toHex = (r2, g2, b2) => `#${[r2, g2, b2].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
            const root = document.documentElement.style;
            root.setProperty('--brand', branding.primary_color);
            root.setProperty('--brand-hover', toHex(mix(r, 0, 0.12), mix(g, 0, 0.12), mix(b, 0, 0.12)));
            root.setProperty('--brand-pressed', toHex(mix(r, 0, 0.25), mix(g, 0, 0.25), mix(b, 0, 0.25)));
            root.setProperty('--brand-soft', toHex(mix(r, 255, 0.9), mix(g, 255, 0.9), mix(b, 255, 0.9)));
            root.setProperty('--brand-soft-2', toHex(mix(r, 255, 0.8), mix(g, 255, 0.8), mix(b, 255, 0.8)));
          }
        }
        if (branding.secondary_color) {
          document.documentElement.style.setProperty('--tenant-secondary', branding.secondary_color);
        }
        if (branding.accent_color) {
          document.documentElement.style.setProperty('--tenant-accent', branding.accent_color);
        }
        if (branding.background_color) {
          document.documentElement.style.setProperty('--tenant-background', branding.background_color);
        }
      } catch (err) {
        console.error('Error loading tenant:', err);
        setError('Restaurant non trouvé');
      } finally {
        setLoading(false);
      }
    };

    loadTenant();
  }, [tenantSlug, isAdminMode]);

  // Helper to get branding value with fallback
  const getBranding = (key) => {
    if (tenant?.branding?.[key]) return tenant.branding[key];
    if (tenant?.[key]) return tenant[key];
    return DEFAULT_BRANDING[key];
  };

  const value = {
    tenant,
    setTenant,
    tenantSlug,
    isAdminMode,
    loading,
    error,
    getBranding,
    // Quick access to common branding values
    tenantName: tenant?.name || tenant?.full_name || DEFAULT_BRANDING.name,
    primaryColor: getBranding('primary_color'),
    secondaryColor: getBranding('secondary_color'),
    // V5.99.17 — Default to null (no logo) → fallback shows the "360" brand.
    // The 🥑 only appears when `branding.logo_type === 'avocado'` is set in DB.
    logoType: tenant?.branding?.logo_type || null,
    currency: tenant?.currency || 'EUR',
    country: tenant?.country || null,
  };
  setCurrencyCountry(tenant?.country);
  setTenantCurrency(tenant?.currency);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
